// src/admin/AdminOrders.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = 'https://madadline.com/api';

const STATUSES = [
  { key: 'all',       label: 'Toutes' },
  { key: 'pending',   label: 'En attente' },
  { key: 'pending_confirmation', label: 'À confirmer' },
  { key: 'confirmed', label: 'Confirmées' },
  { key: 'prepared',  label: 'Préparées' },
  { key: 'shipped',   label: 'Expédiées' },
  { key: 'delivered', label: 'Livrées' },
  { key: 'cancelled', label: 'Annulées' },
];

const STATUS_LABELS = {
  pending:   'En attente',
  pending_confirmation: 'À confirmer',
  confirmed: 'Confirmée',
  prepared:  'Préparée',
  shipped:   'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
};

const PAYMENT_LABELS = {
  cod:      'À la livraison',
  stripe:   'Carte bancaire',
  transfer: 'Virement',
  whatsapp: 'Via WhatsApp',
};

/* ⭐ Les infos du checkout ont TOUJOURS la priorité sur le nom du compte */
const getCustomerName  = (o) => o.customer_name || o.user?.name || '—';
const getCustomerPhone = (o) => o.customer_phone || o.user?.phone || '';
const toWhatsAppNumber = (phone) => (phone || '').replace(/[^\d]/g, '');

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
});

function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');
  const [busyId, setBusyId]   = useState(null);
  const [message, setMessage] = useState(null); // { type: 'ok' | 'error', text }

  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3500);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders`, authHeaders());
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  /* Met à jour une commande dans la liste locale */
  const patchLocal = (id, patch) =>
    setOrders(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o)));

  /* ---------- Changer le statut ---------- */
  const handleStatusChange = async (order, newStatus) => {
    if (newStatus === order.status) return;

    if (newStatus === 'cancelled' &&
        !window.confirm(`Annuler la commande #${order.id} ?`)) return;

    // Livrée + paiement à la livraison non payé → proposer de marquer payée
    const alsoPaid =
      newStatus === 'delivered' &&
      order.payment_status !== 'paid' &&
      window.confirm(`Commande #${order.id} livrée : la marquer aussi comme payée ?`);

    setBusyId(order.id);
    try {
      await axios.put(`${API}/orders/${order.id}`, { status: newStatus }, authHeaders());
      patchLocal(order.id, { status: newStatus });

      if (alsoPaid) {
        await axios.put(`${API}/orders/${order.id}`, { payment_status: 'paid' }, authHeaders());
        patchLocal(order.id, { payment_status: 'paid' });
      }
      flash('ok', `Commande #${order.id} : ${STATUS_LABELS[newStatus]}`);
    } catch (err) {
      console.error(err.response?.data || err);
      flash('error', 'Erreur lors de la mise à jour du statut.');
    } finally {
      setBusyId(null);
    }
  };

  /* ---------- Marquer payée / non payée ---------- */
  const handleTogglePaid = async (order) => {
    const next = order.payment_status === 'paid' ? 'unpaid' : 'paid';
    setBusyId(order.id);
    try {
      await axios.put(`${API}/orders/${order.id}`, { payment_status: next }, authHeaders());
      patchLocal(order.id, { payment_status: next });
      flash('ok', `Commande #${order.id} : ${next === 'paid' ? 'payée' : 'non payée'}`);
    } catch (err) {
      console.error(err.response?.data || err);
      flash('error', 'Erreur lors de la mise à jour du paiement.');
    } finally {
      setBusyId(null);
    }
  };

  /* ---------- Supprimer ---------- */
  const handleDelete = async (order) => {
    const ok = window.confirm(
      `Supprimer définitivement la commande #${order.id} (${getCustomerName(order)}) ?\n` +
      `Cette action est irréversible.`
    );
    if (!ok) return;

    setBusyId(order.id);
    try {
      await axios.delete(`${API}/orders/${order.id}`, authHeaders());
      setOrders(prev => prev.filter(o => o.id !== order.id));
      flash('ok', `Commande #${order.id} supprimée.`);
    } catch (err) {
      console.error(err.response?.data || err);
      flash('error', 'Erreur lors de la suppression.');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = orders.filter(o => {
    if (filter !== 'all' && o.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const match =
        String(o.id).includes(q) ||
        getCustomerName(o).toLowerCase().includes(q) ||
        getCustomerPhone(o).replace(/\s/g, '').includes(q.replace(/\s/g, '')) ||
        (o.city || '').toLowerCase().includes(q) ||
        (o.address || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const totalCA = orders
    .filter(o => o.status !== 'cancelled')
    .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);

  const totalUnpaid = orders
    .filter(o => o.status !== 'cancelled' && o.payment_status !== 'paid')
    .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);

  return (
    <div className="admin-orders">

      <header className="admin-page-head">
        <div>
          <h1>Commandes</h1>
          <p>
            {orders.length} commande{orders.length > 1 ? 's' : ''} · CA : {totalCA.toFixed(0)} DH
            {' '}· À encaisser : {totalUnpaid.toFixed(0)} DH
          </p>
        </div>
      </header>

      {message && (
        <div className={`admin-alert admin-alert--${message.type === 'ok' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      {/* Filtres */}
      <div className="admin-filters">
        <input
          type="text"
          placeholder="Rechercher par n°, nom, téléphone, ville ou adresse..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-input"
        />
        <div className="admin-status-filters">
          {STATUSES.map(s => (
            <button
              key={s.key}
              className={`admin-chip ${filter === s.key ? 'is-active' : ''}`}
              onClick={() => setFilter(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <p className="admin-empty">Aucune commande trouvée.</p>
      ) : (
        <div className="admin-card" style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Cliente (checkout)</th>
                <th>Téléphone</th>
                <th>Ville / Adresse</th>
                <th>Total</th>
                <th>Paiement</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => {
                const name  = getCustomerName(o);
                const phone = getCustomerPhone(o);
                const isGuest = !o.user_id || Boolean(o.user?.is_guest);
                const busy = busyId === o.id;
                const paid = o.payment_status === 'paid';

                return (
                  <tr key={o.id} style={busy ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                    <td><strong>#{o.id}</strong></td>

                    <td>
                      <div className="admin-table__user">
                        <span className="admin-table__avatar">
                          {name.charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <div>{name}</div>
                          <small style={{ color: isGuest ? '#B45309' : '#16A34A' }}>
                            {isGuest ? 'Invitée (sans compte)' : 'Compte client'}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td>
                      {phone ? (
                        <div>
                          <div>{phone}</div>
                          <a
                            href={`https://wa.me/${toWhatsAppNumber(phone)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, color: '#16A34A' }}
                          >
                            WhatsApp
                          </a>
                        </div>
                      ) : '—'}
                    </td>

                    <td>
                      <div>{o.city || '—'}</div>
                      {o.address && <small style={{ color: '#777' }}>{o.address}</small>}
                    </td>

                    <td><strong>{parseFloat(o.total || 0).toFixed(0)} DH</strong></td>

                    {/* Paiement + bouton rapide */}
                    <td>
                      <div>{PAYMENT_LABELS[o.payment_method] || o.payment_method || '—'}</div>
                      <button
                        type="button"
                        onClick={() => handleTogglePaid(o)}
                        title={paid ? 'Marquer comme non payée' : 'Marquer comme payée'}
                        style={{
                          marginTop: 4, padding: '2px 8px', fontSize: 12, borderRadius: 999,
                          cursor: 'pointer', fontWeight: 600,
                          border: `1px solid ${paid ? '#16A34A' : '#B45309'}`,
                          background: paid ? '#DCFCE7' : '#FEF3C7',
                          color: paid ? '#16A34A' : '#B45309',
                        }}
                      >
                        {paid ? '✓ Payée' : 'Marquer payée'}
                      </button>
                    </td>

                    {/* Statut modifiable directement */}
                    <td>
                      <select
                        value={o.status || 'pending'}
                        onChange={e => handleStatusChange(o, e.target.value)}
                        className={`admin-badge admin-badge--${o.status || 'pending'}`}
                        style={{ border: 'none', cursor: 'pointer', paddingRight: 6 }}
                        aria-label={`Statut de la commande ${o.id}`}
                      >
                        {Object.entries(STATUS_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </td>

                    <td>{new Date(o.created_at).toLocaleDateString('fr-FR')}</td>

                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => navigate(`/admin/commandes/${o.id}`)}
                          className="admin-btn-sm admin-btn-sm--edit"
                        >
                          Détails
                        </button>
                        <button
                          onClick={() => handleDelete(o)}
                          className="admin-btn-sm admin-btn-sm--delete"
                          style={{ color: '#DC2626' }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;